import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { trackSegment } from '~/components/SegmentAnalytics';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { UserAttributes } from '~/redux/api/methods/roles/roles';
import { ParsedType } from '~/redux/api/transformers/utils';
import { coerceRtkqError } from '~/redux/api/utils';
import { Nullable } from '~/types/Nullable';

type Props = {
  user: Nullable<ParsedType<UserAttributes>>;
  isVisible: boolean;
  onHide: () => void;
};

export const DeactivateUserModal = ({ user, isVisible, onHide }: Props) => {
  const intl = useIntl();
  const [deactivateUser, deactivateUserResult] =
    api.endpoints.deactivateSuperAdmin.useMutation();
  const { toast } = useToast();
  const { isError, isLoading, isSuccess, error } = deactivateUserResult;
  const currentUser = useCurrentUser();

  useEffect(() => {
    if (isSuccess) {
      toast({
        message: intl.formatMessage(
          {
            id: 'SuperAdminDeactivateUserModal.successMessage',
            defaultMessage: '"{firstName} {lastName}" deactivated.',
            description:
              'Success message when super admin user is deactivated. Example: firstName = "John", lastName = "Smith"',
          },
          { firstName: user?.firstName, lastName: user?.lastName },
        ),
        status: 'success',
      });
      onHide();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      const resp = coerceRtkqError(error)?.response || {};
      const msg = resp.data?.errors?.[0];
      toast({
        message:
          msg?.title ||
          intl.formatMessage({
            id: 'SuperAdminDeactivateUserModal.errorMessage',
            defaultMessage: 'An error occurred while deleting the user.',
            description:
              'Error message when super admin user deactivation fails',
          }),
        status: 'danger',
      });
    }
  }, [isError]);

  return (
    <Modal
      isVisible={isVisible}
      title={intl.formatMessage({
        id: 'SuperAdminDeactivateUserModal.title',
        defaultMessage: 'Deactivate User?',
        description: 'Modal title for super admin user deactivation',
      })}
      onCancel={onHide}
      onHide={onHide}
      primaryAction={{
        text: intl.formatMessage({
          id: 'SuperAdminDeactivateUserModal.deactivateButton',
          defaultMessage: 'Deactivate',
          description: 'Button text for super admin user deactivation',
        }),
        onClick: () => {
          trackSegment('submitDeleteUser', {
            id: user!.id,
            user_id: currentUser.id,
            client_id: currentUser.client,
          });
          deactivateUser({ superAdminId: user!.id });
        },
        isLoading,
        variant: 'danger',
      }}
    >
      <Text>
        <FormattedMessage
          id="SuperAdminDeactivateUserModal.confirmationText"
          defaultMessage='Are you sure you want to deactivate "{firstName} {lastName}"? Users can be reactivated at any time on the Users page.'
          description='Confirmation message for deactivating a super admin user. Example: firstName = "John", lastName = "Smith"'
          values={{
            firstName: user?.firstName,
            lastName: user?.lastName,
          }}
        />
      </Text>
    </Modal>
  );
};
